import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { type CSSProperties, useEffect, useState } from 'react';

import { AppLogo } from '@/assets/icons/AppLogo';
import spritesBg from '@/assets/images/whats-new/sprites-creative.webp';

type SpriteKind = 'website' | 'pdf';
type PreviewKind =
  | 'chart'
  | 'slides'
  | 'dashboard'
  | 'evite'
  | 'nodes'
  | 'doc'
  | 'report'
  | 'resume';

type Form = {
  label: string;
  accent: string;
  kind: SpriteKind;
  preview: PreviewKind;
};

const FORMS: Form[] = [
  { label: 'Visualize', accent: '#5b6cff', kind: 'website', preview: 'chart' },
  { label: 'Present', accent: '#e0a53a', kind: 'website', preview: 'slides' },
  {
    label: 'Dashboard',
    accent: '#3da675',
    kind: 'website',
    preview: 'dashboard',
  },
  { label: 'Evite', accent: '#2f9bd8', kind: 'website', preview: 'evite' },
  { label: 'Explain', accent: '#8b6cff', kind: 'website', preview: 'nodes' },
  { label: 'Brief', accent: '#e05252', kind: 'pdf', preview: 'doc' },
  { label: 'Report', accent: '#d0603a', kind: 'pdf', preview: 'report' },
  { label: 'Notes', accent: '#4a90e2', kind: 'pdf', preview: 'doc' },
  { label: 'Resume', accent: '#2fae8f', kind: 'pdf', preview: 'resume' },
];

type Prompt = {
  text: string;
  color: string;
  top: string;
  left: string;
  delay: number;
};
const PROMPTS: Prompt[] = [
  {
    text: 'Present my research…',
    color: '#5b6cff',
    top: '5%',
    left: '5%',
    delay: 0,
  },
  {
    text: 'Build a report on Q3…',
    color: '#e05252',
    top: '7%',
    left: '54%',
    delay: 0.8,
  },
  {
    text: 'Design an invite for…',
    color: '#2f9bd8',
    top: '19%',
    left: '8%',
    delay: 1.4,
  },
  {
    text: 'Visualize this data…',
    color: '#3da675',
    top: '22%',
    left: '56%',
    delay: 0.4,
  },
];

// Dotted connector paths in the panel's fixed 448x640 viewBox space.
const PATHS: string[] = [
  'M100,64 C 182,118 214,208 200,262',
  'M330,80 C 248,132 236,208 250,262',
  'M112,160 C 150,192 186,230 196,266',
  'M330,174 C 290,202 256,236 246,266',
  'M224,338 C 224,372 224,398 224,430',
];

const CYCLE_MS = 2800;

const line = (w: string, c = '#e6e7ee'): CSSProperties => ({
  width: w,
  height: 5,
  borderRadius: 3,
  background: c,
  margin: '5px 12px',
});

const Thumb = ({ form }: { form: Form }) => {
  const a = form.accent;
  switch (form.preview) {
    case 'chart':
      return (
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'center',
            gap: 7,
            height: 72,
            padding: '12px 16px 10px',
          }}
        >
          {[50, 72, 90, 100].map((h, i) => (
            <div
              key={i}
              style={{
                width: 12,
                height: `${h}%`,
                borderRadius: '3px 3px 0 0',
                background: i === 3 ? a : `${a}66`,
              }}
            />
          ))}
        </div>
      );
    case 'slides':
      return (
        <>
          <div
            style={{
              height: 28,
              borderRadius: 5,
              margin: '12px 12px 6px',
              background: 'linear-gradient(135deg,#2a2550,#14122b)',
            }}
          />
          <div style={line('45%', '#e7e2d0')} />
          <div style={line('66%', '#e7e2d0')} />
        </>
      );
    case 'dashboard':
      return (
        <>
          <div style={{ display: 'flex', gap: 6, padding: '12px 12px 6px' }}>
            {[a, '#e0a53a', '#2f9bd8'].map((c) => (
              <div
                key={c}
                style={{
                  flex: 1,
                  height: 26,
                  borderRadius: 5,
                  background: `${c}22`,
                  border: `1px solid ${c}44`,
                }}
              />
            ))}
          </div>
          <svg
            viewBox="0 0 100 30"
            preserveAspectRatio="none"
            style={{ height: 26, margin: '0 12px', width: 'calc(100% - 24px)' }}
          >
            <polyline
              points="0,24 20,16 40,20 60,8 80,12 100,4"
              fill="none"
              stroke={a}
              strokeWidth={2}
            />
          </svg>
        </>
      );
    case 'evite':
      return (
        <div style={{ paddingBottom: 6 }}>
          <div
            style={{
              height: 30,
              borderRadius: 6,
              margin: '12px 12px 10px',
              background: `linear-gradient(135deg, ${a}, ${a}99)`,
            }}
          />
          <div
            style={{
              width: '46%',
              height: 7,
              borderRadius: 4,
              background: `${a}33`,
              margin: '0 auto 8px',
            }}
          />
          <div
            style={{
              width: '58%',
              height: 5,
              borderRadius: 3,
              background: '#e6e7ee',
              margin: '0 auto 5px',
            }}
          />
          <div
            style={{
              width: '34%',
              height: 5,
              borderRadius: 3,
              background: '#e6e7ee',
              margin: '0 auto',
            }}
          />
        </div>
      );
    case 'nodes':
      return (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 14,
            height: 72,
          }}
        >
          <span
            style={{
              width: 22,
              height: 22,
              borderRadius: 9999,
              background: `${a}22`,
              border: `1px solid ${a}`,
            }}
          />
          <span style={{ width: 20, height: 1.5, background: `${a}66` }} />
          <span
            style={{
              width: 22,
              height: 22,
              borderRadius: 9999,
              background: `${a}22`,
              border: `1px solid ${a}`,
            }}
          />
          <span style={{ width: 20, height: 1.5, background: `${a}66` }} />
          <span
            style={{ width: 22, height: 22, borderRadius: 9999, background: a }}
          />
        </div>
      );
    case 'report':
      return (
        <div style={{ padding: '10px 0 4px' }}>
          <div
            style={{
              width: '38%',
              height: 7,
              borderRadius: 3,
              background: a,
              margin: '0 12px 8px',
            }}
          />
          <div style={{ display: 'flex', gap: 8, padding: '0 12px' }}>
            {[0, 1].map((k) => (
              <div key={k} style={{ flex: 1 }}>
                <div style={{ ...line('100%'), margin: '0 0 5px' }} />
                <div style={{ ...line('80%'), margin: '0 0 5px' }} />
                <div style={{ ...line('90%'), margin: 0 }} />
              </div>
            ))}
          </div>
        </div>
      );
    case 'resume':
      return (
        <div style={{ display: 'flex', gap: 10, padding: 12 }}>
          <span
            style={{
              width: 26,
              height: 26,
              borderRadius: 9999,
              background: `${a}33`,
              border: `1px solid ${a}`,
              flexShrink: 0,
            }}
          />
          <div style={{ flex: 1 }}>
            <div style={line('60%', a)} />
            <div style={line('40%')} />
            <div style={{ height: 6 }} />
            <div style={line('90%')} />
            <div style={line('75%')} />
          </div>
        </div>
      );
    case 'doc':
      return (
        <div style={{ padding: '12px 0 4px' }}>
          <div
            style={{
              width: '40%',
              height: 8,
              borderRadius: 3,
              background: a,
              margin: '0 12px 8px',
            }}
          />
          <div style={line('82%')} />
          <div style={line('72%')} />
          <div style={line('78%')} />
          <div style={line('50%')} />
        </div>
      );
    default: {
      const _exhaustive: never = form.preview;
      return _exhaustive;
    }
  }
};

const ShareIcon = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#1a1a1f"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="6" cy="12" r="2.4" />
    <circle cx="17.5" cy="6" r="2.4" />
    <circle cx="17.5" cy="18" r="2.4" />
    <path d="m8.3 10.8 6.9-3.6M8.3 13.2l6.9 3.6" />
  </svg>
);

const DownloadIcon = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#1a1a1f"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 3v11" />
    <path d="m7.5 10 4.5 4.5 4.5-4.5" />
    <path d="M5 19.5h14" />
  </svg>
);

export const SpritesSpotlight = () => {
  const reduceMotion = useReducedMotion();
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (reduceMotion) return;
    const id = window.setInterval(
      () => setIndex((i) => (i + 1) % FORMS.length),
      CYCLE_MS
    );
    return () => window.clearInterval(id);
  }, [reduceMotion]);

  const form = FORMS[index];
  const isWebsite = form.kind === 'website';

  return (
    <div className="relative size-full overflow-hidden bg-[#0b341f] max-sm:[&>:not(img)]:hidden max-sm:[&>img]:object-[center_58%]">
      <img
        src={spritesBg}
        alt=""
        className="pointer-events-none absolute inset-0 size-full object-cover"
        draggable={false}
      />
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(120% 90% at 50% 46%, rgba(0,0,0,0.28), rgba(0,0,0,0.05) 60%)',
        }}
      />

      <svg
        className="pointer-events-none absolute inset-0 size-full"
        viewBox="0 0 448 640"
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        {PATHS.map((d, i) => (
          <motion.path
            key={i}
            d={d}
            fill="none"
            stroke="rgba(255,255,255,0.7)"
            strokeWidth={1.6}
            strokeLinecap="round"
            strokeDasharray="2 5"
            vectorEffect="non-scaling-stroke"
            animate={reduceMotion ? undefined : { strokeDashoffset: [0, -7] }}
            transition={{ duration: 0.9, ease: 'linear', repeat: Infinity }}
          />
        ))}
      </svg>

      {PROMPTS.map((p) => (
        <motion.div
          key={p.text}
          className="absolute z-[15] flex items-center gap-1.5 whitespace-nowrap rounded-xl border border-white/70 bg-white/95 px-2.5 py-1.5 text-[11.5px] font-medium text-[#26251f] shadow-[0_10px_20px_rgba(0,0,0,0.22)]"
          style={{ top: p.top, left: p.left }}
          animate={reduceMotion ? undefined : { y: [0, -4, 0] }}
          transition={{
            duration: 5,
            ease: 'easeInOut',
            repeat: Infinity,
            delay: p.delay,
          }}
        >
          <span
            className="size-[5px] shrink-0 rounded-full"
            style={{ background: p.color }}
          />
          {p.text}
        </motion.div>
      ))}

      <div
        className="absolute left-1/2 top-[46%] z-10 -translate-x-1/2 -translate-y-1/2 rounded-full border border-white/15 bg-black/12 backdrop-blur-[2px]"
        style={{ width: '26%', aspectRatio: '1 / 1' }}
      />
      <div
        className="absolute left-1/2 top-[46%] z-20 flex -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-[18%] border-2 border-[rgba(212,212,183,0.55)] bg-white shadow-[0_22px_34px_rgba(0,0,0,0.28)]"
        style={{ width: '14%', aspectRatio: '1 / 1' }}
      >
        <AppLogo size="62%" customColor="#0a0a0a" />
      </div>

      <div
        className="absolute left-1/2 top-[80%] z-30 grid -translate-x-1/2 -translate-y-1/2"
        style={{ width: '56%' }}
      >
        <AnimatePresence>
          <motion.div
            key={form.label}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6, ease: 'easeInOut' }}
            className="overflow-hidden rounded-2xl border border-white/70 bg-white [grid-area:1/1]"
          >
            <div className="flex items-center px-3 pb-1.5 pt-2.5">
              <div className="flex items-center gap-1.5">
                <span
                  className="size-2 rounded-full"
                  style={{ background: form.accent }}
                />
                <span className="text-[11px] font-bold uppercase tracking-wider text-[#6a6a73]">
                  {form.label}
                </span>
              </div>
            </div>
            <div className="mx-3 mb-2 h-[86px] overflow-hidden rounded-lg border border-[#eee] bg-[#fbfbfd]">
              <Thumb form={form} />
            </div>
            <div className="flex items-center justify-between px-3 pb-2.5 pt-0.5">
              <span className="rounded-md bg-[#f2f2f5] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#6a6a73]">
                {isWebsite ? 'HTML' : 'PDF'}
              </span>
              {isWebsite ? (
                <span
                  className="flex size-6 shrink-0 items-center justify-center rounded-full border border-[#d9d7cf] bg-white"
                  aria-label="Share"
                >
                  <ShareIcon />
                </span>
              ) : (
                <span
                  className="flex size-6 shrink-0 items-center justify-center rounded-full border border-[#d9d7cf] bg-white"
                  aria-label="Download"
                >
                  <DownloadIcon />
                </span>
              )}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};
